/**
 * OtisVisibilityContext — lets focused, full-screen surfaces temporarily hide
 * the floating Otis launcher.
 *
 * The launcher (OtisWidget) is mounted once globally and normally shows on every
 * authenticated screen. Some surfaces open as in-place modals (no route change),
 * so the widget — which only watches the route — can't tell it should step out
 * of the way. The tech job-detail overlay is the main one: when a tech is heads-
 * down in a job, Otis floating over the work is clutter, not help.
 *
 * Any component can call `useSuppressOtis(active)` to hide the launcher while it
 * is mounted (or while `active` is true). Suppressors are reference-counted so
 * nested / overlapping callers compose cleanly.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

interface OtisVisibilityValue {
  suppressedCount: number;
  pushSuppressor: () => void;
  popSuppressor: () => void;
}

const OtisVisibilityContext = createContext<OtisVisibilityValue | null>(null);

export function OtisVisibilityProvider({ children }: { children: ReactNode }) {
  const [suppressedCount, setSuppressedCount] = useState(0);
  const pushSuppressor = useCallback(() => setSuppressedCount((c) => c + 1), []);
  const popSuppressor = useCallback(
    () => setSuppressedCount((c) => Math.max(0, c - 1)),
    [],
  );

  return (
    <OtisVisibilityContext.Provider
      value={{ suppressedCount, pushSuppressor, popSuppressor }}
    >
      {children}
    </OtisVisibilityContext.Provider>
  );
}

/** True while any consumer has requested the Otis launcher be hidden. */
export function useOtisSuppressed(): boolean {
  const ctx = useContext(OtisVisibilityContext);
  return ctx ? ctx.suppressedCount > 0 : false;
}

/**
 * Hide the floating Otis launcher while `active` is true (mount-scoped).
 * Reference-counted, so multiple suppressors are safe. No-op when rendered
 * outside an OtisVisibilityProvider.
 */
export function useSuppressOtis(active: boolean = true): void {
  const ctx = useContext(OtisVisibilityContext);
  useEffect(() => {
    if (!ctx || !active) return;
    ctx.pushSuppressor();
    return () => ctx.popSuppressor();
  }, [ctx, active]);
}
